import React from 'react';
import {
  TextField,
  Box,
  Typography,
  Chip,
  Stack,
  Paper,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  TextFields as TextIcon,
  Info,
} from '@mui/icons-material';
import BaseModal from '../BaseModal';
import { CustomButton } from '../../Infra/CustomButton/CustomButton';
import { TextMatchingModalProps } from './types';
import { useTextMatching } from './useTextMatching';
import CurrencySelector from './CurrencySelector';
import FeeTypeSelector from './FeeTypeSelector';
import { CURRENCIES } from './currencyData';

const TextMatchingModal: React.FC<TextMatchingModalProps> = ({
  open,
  onClose,
  onSave,
  initialRules = [],
  fieldName,
  category,
}) => {
  const {
    rules,
    getInputValue,
    setInputValue,
    usePredefinedBlocks,
    isCurrency,
    isFeeType,
    addMappedValue,
    removeMappedValue,
    deleteRule,
    addCurrencyRule,
    addFeeTypeRule,
    handleKeyPress,
    handlePaste,
    getValidRules,
    hasValidRules,
    getPlaceholderForRule,
  } = useTextMatching(open, initialRules, category, fieldName);

  const handleSave = () => {
    const validRules = getValidRules();
    onSave(validRules);
    onClose();
  };

  return (
    <BaseModal
      dataTestId="text-matching-modal"
      open={open}
      onClose={onClose}
      onSubmit={handleSave}
      title={`Text Matching Configuration - ${fieldName}`}
      icon={<TextIcon />}
      submitText="Save Configuration"
      submitDisabled={!hasValidRules}
      maxWidth="sm"
    >
      <Stack spacing={3}>
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {usePredefinedBlocks
              ? 'Configure text variations'
              : 'Why configure text mapping?'}
          </Typography>
          <Typography variant="body2">
            {usePredefinedBlocks
              ? `Add different text variations that should be recognized as ${fieldName} values. The system has predefined standard values that cannot be changed.`
              : 'Your Excel file may contain variations of the same value (like "Buy", "BUY", "קנה"). This tool maps all variations to a single standard value for consistent processing.'}
          </Typography>
          {fieldName === 'Deposit/Withdraw' &&
            category === 'Deposits and Withdrawals' && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                Note: For this field, text matching will be based on the
                combined values from both configured columns.
              </Typography>
            )}
        </Alert>

        <Paper variant="text" sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Info color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {usePredefinedBlocks
                ? 'Predefined Standard Values'
                : 'Configure Text Mapping Rules'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {usePredefinedBlocks
              ? 'Add variations for each standard value below.'
              : 'Each rule defines how different text variations should be standardized.'}
          </Typography>

          {/* Add New Currency Section - For currency fields at the top */}
          {isCurrency && (
            <Box sx={{ mb: 3 }}>
              <CurrencySelector
                onAddCurrency={addCurrencyRule}
                existingCurrencies={rules.map(r => r.standardValue)}
              />
            </Box>
          )}

          {/* Add Fee Type Section - For fee type fields at the top */}
          {isFeeType && (
            <Box sx={{ mb: 3 }}>
              <FeeTypeSelector
                onAddFeeType={addFeeTypeRule}
                existingFeeTypes={rules.map(r => r.standardValue)}
              />
            </Box>
          )}

          {rules.length === 0 ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                {isCurrency
                  ? 'No currencies configured'
                  : isFeeType
                    ? 'No fee types configured'
                    : 'No rules configured yet'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isCurrency
                  ? 'Add currencies from the dropdown above to get started.'
                  : isFeeType
                    ? 'Add fee types from the dropdown above to get started.'
                    : usePredefinedBlocks
                      ? 'ERROR: No predefined blocks found for this field.'
                      : 'Add new rules to get started.'}
              </Typography>
            </Box>
          ) : (
            <Stack spacing={3}>
              {rules.map((rule, ruleIndex) => (
                <Paper
                  key={ruleIndex}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    backgroundColor: rule.isPredefined ? 'grey.50' : 'white',
                  }}
                  variant="text"
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {(() => {
                        if (isCurrency && rule.standardValue) {
                          const currency = CURRENCIES.find(
                            c => c.name === rule.standardValue
                          );
                          if (currency) {
                            return `${rule.standardValue} (${currency.symbol})`;
                          }
                        }
                        return (
                          rule.standardValue ||
                          `Standard Value ${ruleIndex + 1}`
                        );
                      })()}
                    </Typography>
                    {(isCurrency || isFeeType || !rule.isPredefined) && (
                      <IconButton
                        size="small"
                        onClick={() => deleteRule(ruleIndex)}
                        sx={{ color: 'error.main' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      mb: 2,
                      alignItems: 'center',
                    }}
                  >
                    <TextField
                      size="small"
                      placeholder={
                        getPlaceholderForRule(ruleIndex) +
                        ' (Tip: Paste multiple Excel cells)'
                      }
                      value={getInputValue(ruleIndex)}
                      onChange={e => {
                        setInputValue(ruleIndex, e.target.value);
                      }}
                      onKeyPress={e => handleKeyPress(e, ruleIndex)}
                      onPaste={e => handlePaste(e, ruleIndex)}
                      sx={{ flexGrow: 1 }}
                      helperText={
                        getInputValue(ruleIndex) &&
                        rule.mappedValues.includes(
                          getInputValue(ruleIndex).trim()
                        )
                          ? `"${getInputValue(ruleIndex).trim()}" already exists`
                          : undefined
                      }
                      error={
                        !!getInputValue(ruleIndex) &&
                        rule.mappedValues.includes(
                          getInputValue(ruleIndex).trim()
                        )
                      }
                    />
                    <CustomButton
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        addMappedValue(ruleIndex, getInputValue(ruleIndex));
                      }}
                      size="small"
                      disabled={
                        !getInputValue(ruleIndex).trim() ||
                        rule.mappedValues.includes(
                          getInputValue(ruleIndex).trim()
                        )
                      }
                    >
                      Add
                    </CustomButton>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {rule.mappedValues.map((value, valueIndex) => (
                      <Chip
                        key={valueIndex}
                        label={value}
                        onDelete={() =>
                          removeMappedValue(ruleIndex, valueIndex)
                        }
                        size="small"
                        sx={{
                          bgcolor: 'grey.100',
                          color: 'grey.800',
                          borderColor: 'grey.300',
                          '&:hover': {
                            bgcolor: 'grey.200',
                          },
                          '& .MuiChip-deleteIcon': {
                            color: 'grey.600',
                            '&:hover': {
                              color: 'grey.800',
                            },
                          },
                        }}
                        variant="text"
                      />
                    ))}
                  </Box>
                </Paper>
              ))}
            </Stack>
          )}
        </Paper>
      </Stack>
    </BaseModal>
  );
};

export default TextMatchingModal;
